import { Phone, Mail, MessageCircle, Send, MapPin, Users, Clock, CheckCircle } from 'lucide-react';
import { useState } from 'react';

export function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    checkIn: '',
    checkOut: '',
    guests: '1',
    roomType: '',
    message: '',
  });

  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Create WhatsApp message
    const message = `🏨 *New Luxury Booking Inquiry - Hotel A.S.K Grand*

👤 *Distinguished Guest Details:*
Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}

📅 *Luxury Stay Details:*
Check-in: ${formData.checkIn || 'Not specified'}
Check-out: ${formData.checkOut || 'Not specified'}
Guests: ${formData.guests}
Suite Type: ${formData.roomType || 'Any available luxury suite'}

💬 *Special Requests:*
${formData.message || 'None'}

Please confirm availability and exclusive VIP rates. Thank you!`;

    const whatsappUrl = `https://wa.me/917799933325?text=${encodeURIComponent(message)}`;
    
    // Simulate form submission delay
    setTimeout(() => {
      window.open(whatsappUrl, '_blank');
      setSubmitted(true);
      setIsSubmitting(false);
      
      // Reset form after success
      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          checkIn: '',
          checkOut: '',
          guests: '1',
          roomType: '',
          message: '',
        });
      }, 3000);
    }, 1000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleQuickContact = (method: string) => {
    if (method === 'call') {
      window.location.href = 'tel:+917799933325';
    } else if (method === 'whatsapp') {
      const quickMessage = 'Hi! I would like to inquire about luxury suite availability at Hotel A.S.K Grand.';
      window.open(`https://wa.me/917799933325?text=${encodeURIComponent(quickMessage)}`, '_blank');
    }
  };

  return (
    <section id="contact" className="py-16 sm:py-24 bg-luxury-gradient relative overflow-hidden">
      {/* Luxury Background Pattern */}
      <div className="absolute inset-0 bg-luxury-pattern opacity-30"></div>
      
      <div className="relative w-full px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block glass-luxury border border-gold-400/30 text-gold-300 px-6 py-3 rounded-full text-sm font-medium mb-6">
            Reserve Your Luxury Experience
          </div>
          <h2 className="mb-6 text-white">Ready to Experience Unparalleled Luxury?</h2>
          <p className="text-cream-200 max-w-3xl mx-auto text-lg">
            Contact us through any of the methods below or fill out our exclusive booking form. Our luxury concierge team is available 24/7 to assist you with your reservation.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 mb-16">
          {/* Contact Methods - Left Sidebar */}
          <div className="lg:col-span-2 space-y-6">
            <h3 className="text-2xl font-bold text-gold-400 mb-8">Luxury Concierge Contact</h3>
            
            {/* Quick Contact Cards */}
            <div
              onClick={() => handleQuickContact('call')}
              className="group cursor-pointer glass-luxury rounded-3xl p-8 shadow-luxury-xl hover:shadow-gold-xl transition-all duration-300 transform hover:-translate-y-2 border border-gold-400/20 hover:border-gold-400/50"
            >
              <div className="flex items-center gap-6">
                <div className="w-16 h-16 bg-gold-gradient rounded-3xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-gold">
                  <Phone className="w-8 h-8 text-navy-900" />
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-gold-400 text-lg group-hover:text-gold-300 transition-colors">Call Luxury Concierge</h4>
                  <p className="text-cream-200 font-medium text-lg">+91 7799933325</p>
                  <p className="text-sm text-cream-300">Available 24/7 for VIP service</p>
                </div>
              </div>
            </div>

            <div
              onClick={() => handleQuickContact('whatsapp')}
              className="group cursor-pointer glass-luxury rounded-3xl p-8 shadow-luxury-xl hover:shadow-gold-xl transition-all duration-300 transform hover:-translate-y-2 border border-gold-400/20 hover:border-gold-400/50"
            >
              <div className="flex items-center gap-6">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-700 rounded-3xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <MessageCircle className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-gold-400 text-lg group-hover:text-gold-300 transition-colors">WhatsApp Luxury Chat</h4>
                  <p className="text-cream-200 font-medium">Instant luxury consultation</p>
                  <p className="text-sm text-cream-300">Immediate VIP response</p>
                </div>
              </div>
            </div>

            <div className="glass-luxury rounded-3xl p-8 shadow-luxury border border-gold-400/20">
              <div className="flex items-center gap-6">
                <div className="w-16 h-16 bg-gradient-to-br from-navy-600 to-navy-800 rounded-3xl flex items-center justify-center flex-shrink-0 shadow-luxury">
                  <Mail className="w-8 h-8 text-gold-400" />
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-gold-400 text-lg">Email Concierge</h4>
                  <p className="text-cream-200 font-medium text-sm">askgrandgajuwaka@gmail.com</p>
                  <p className="text-sm text-cream-300">VIP response within 1 hour</p>
                </div>
              </div>
            </div>

            <div className="glass-luxury rounded-3xl p-8 shadow-luxury border border-gold-400/20">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-gold-gradient rounded-3xl flex items-center justify-center flex-shrink-0 shadow-luxury">
                  <MapPin className="w-8 h-8 text-gold-400" />
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-gold-400 text-lg">Visit Our Luxury Hotel</h4>
                  <p className="text-cream-200 text-sm leading-relaxed">
                    10-2-9/1, Mehar Nagar<br />
                    Old Gajuwaka, Visakhapatnam<br />
                    Andhra Pradesh 530026, India
                  </p>
                </div>
              </div>
            </div>

            {/* Operating Hours */}
            <div className="bg-gold-gradient rounded-3xl p-8 text-navy-900 shadow-gold-xl">
              <div className="flex items-center gap-4 mb-6">
                <Clock className="w-7 h-7" />
                <h4 className="font-bold text-xl">Luxury Service Hours</h4>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="font-medium">Concierge Desk:</span>
                  <span className="font-bold">24/7 VIP Service</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Check-in:</span>
                  <span className="font-bold">12:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Check-out:</span>
                  <span className="font-bold">11:00 AM</span>
                </div>
              </div>
            </div>
          </div>

          {/* Booking Form - Right Side */}
          <div className="lg:col-span-3">
            <div className="glass-luxury rounded-3xl shadow-luxury-xl p-8 md:p-12 border border-gold-400/20">
              <div className="text-center mb-10">
                <h3 className="text-3xl font-bold text-gold-400 mb-3">Reserve Your Luxury Suite</h3>
                <p className="text-cream-200 text-lg">Fill out the form below and our concierge will contact you instantly</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Personal Information */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-bold text-gold-400 mb-3">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-5 py-4 bg-white/10 border-2 border-gold-400/30 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-gold-400 transition-colors text-white placeholder-cream-300 backdrop-blur-sm"
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-bold text-gold-400 mb-3">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-5 py-4 bg-white/10 border-2 border-gold-400/30 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-gold-400 transition-colors text-white placeholder-cream-300 backdrop-blur-sm"
                      placeholder="+91 XXXXXXXXXX"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-bold text-gold-400 mb-3">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-5 py-4 bg-white/10 border-2 border-gold-400/30 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-gold-400 transition-colors text-white placeholder-cream-300 backdrop-blur-sm"
                    placeholder="your@email.com"
                  />
                </div>

                {/* Booking Details */}
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <label htmlFor="checkIn" className="block text-sm font-bold text-gold-400 mb-3">
                      Check-in Date
                    </label>
                    <input
                      type="date"
                      id="checkIn"
                      name="checkIn"
                      value={formData.checkIn}
                      onChange={handleChange}
                      className="w-full px-5 py-4 bg-white/10 border-2 border-gold-400/30 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-gold-400 transition-colors text-white backdrop-blur-sm date-input"
                    />
                  </div>

                  <div>
                    <label htmlFor="checkOut" className="block text-sm font-bold text-gold-400 mb-3">
                      Check-out Date
                    </label>
                    <input
                      type="date"
                      id="checkOut"
                      name="checkOut"
                      value={formData.checkOut}
                      onChange={handleChange}
                      className="w-full px-5 py-4 bg-white/10 border-2 border-gold-400/30 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-gold-400 transition-colors text-white backdrop-blur-sm date-input"
                    />
                  </div>

                  <div>
                    <label htmlFor="guests" className="block text-sm font-bold text-gold-400 mb-3">
                      Guests
                    </label>
                    <div className="relative">
                      <select
                        id="guests"
                        name="guests"
                        value={formData.guests}
                        onChange={handleChange}
                        className="w-full px-5 py-4 bg-white/10 border-2 border-gold-400/30 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-gold-400 transition-colors appearance-none text-white backdrop-blur-sm custom-select"
                      >
                        {[1, 2, 3, 4].map((num) => (
                          <option key={num} value={num} className="bg-navy-900 text-white">
                            {num} {num === 1 ? 'Guest' : 'Guests'}
                          </option>
                        ))}
                      </select>
                      <Users className="absolute right-4 top-4 w-5 h-5 text-gold-400 pointer-events-none" />
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="roomType" className="block text-sm font-bold text-gold-400 mb-3">
                    Preferred Suite Type
                  </label>
                  <select
                    id="roomType"
                    name="roomType"
                    value={formData.roomType}
                    onChange={handleChange}
                    className="w-full px-5 py-4 bg-white/10 border-2 border-gold-400/30 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-gold-400 transition-colors text-white backdrop-blur-sm"
                  >
                    <option value="" className="bg-navy-900 text-white">Any Available Luxury Suite</option>
                    <option value="Single Room" className="bg-navy-900 text-white">Single Luxury Room</option>
                    <option value="Royal Room" className="bg-navy-900 text-white">Royal Luxury Room</option>
                    <option value="Suite Room" className="bg-navy-900 text-white">Premium Suite Room</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-bold text-gold-400 mb-3">
                    Special Luxury Requests (Optional)
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-5 py-4 bg-white/10 border-2 border-gold-400/30 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-gold-400 transition-colors resize-none text-white placeholder-cream-300 backdrop-blur-sm"
                    placeholder="Any special requests, dietary requirements, or additional luxury services needed?"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || submitted}
                  className={`w-full py-5 rounded-2xl font-bold transition-all duration-300 flex items-center justify-center gap-4 text-lg ${
                    submitted
                      ? 'bg-green-500 text-white shadow-lg'
                      : isSubmitting
                      ? 'bg-cream-400 text-navy-900 cursor-not-allowed'
                      : 'bg-gold-gradient text-navy-900 hover:shadow-gold-xl transform hover:scale-105 pulse-luxury'
                  }`}
                >
                  {submitted ? (
                    <>
                      <CheckCircle className="w-7 h-7" />
                      <span>Luxury Booking Request Sent!</span>
                    </>
                  ) : isSubmitting ? (
                    <>
                      <div className="w-6 h-6 border-2 border-navy-900 border-t-transparent rounded-full animate-spin"></div>
                      <span>Sending Luxury Request...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-6 h-6" />
                      <span>Send Luxury Booking Request</span>
                    </>
                  )}
                </button>

                <p className="text-cream-300 text-sm text-center">
                  By submitting this form, your details will be sent via WhatsApp to our luxury concierge team for instant VIP confirmation.
                </p>
              </form>
            </div>
          </div>
        </div>

        {/* Hotel Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="glass-luxury rounded-3xl p-8 text-center shadow-luxury hover:shadow-gold-xl transition-shadow border border-gold-400/20">
            <p className="text-2xl font-semibold text-gold-400 mb-3">Premium</p>
            <p className="text-cream-300 font-medium">Luxury Suites</p>
          </div>
          <div className="glass-luxury rounded-3xl p-8 text-center shadow-luxury hover:shadow-gold-xl transition-shadow border border-gold-400/20">
            <div className="text-4xl font-bold text-gold-400 mb-3">4.8★</div>
            <p className="text-cream-300 font-medium">VIP Guest Rating</p>
          </div>
          <div className="glass-luxury rounded-3xl p-8 text-center shadow-luxury hover:shadow-gold-xl transition-shadow border border-gold-400/20">
            <div className="text-4xl font-bold text-gold-400 mb-3">5km</div>
            <p className="text-cream-300 font-medium">From Airport</p>
          </div>
          <div className="glass-luxury rounded-3xl p-8 text-center shadow-luxury hover:shadow-gold-xl transition-shadow border border-gold-400/20">
            <div className="text-4xl font-bold text-gold-400 mb-3">24/7</div>
            <p className="text-cream-300 font-medium">Concierge Service</p>
          </div>
        </div>
      </div>
    </section>
  );
}
